import type { EegBand } from "@musejs/signal";

/**
 * A rolling window per channel, kept as plain arrays.
 *
 * The monitors in `@musejs/features` hold their own history, but the frequency
 * view needs the raw window to transform, and the heart view needs a longer one
 * than either. Keeping one buffer here avoids each view growing its own.
 */
export class ChannelBuffer {
  readonly #capacity: number;
  readonly #columns = new Map<string, number[]>();
  #sampleRate = 0;

  constructor(seconds: number, assumedRate: number) {
    this.#capacity = Math.round(seconds * assumedRate);
  }

  get sampleRate(): number {
    return this.#sampleRate;
  }

  get channels(): readonly string[] {
    return [...this.#columns.keys()];
  }

  push(channels: Readonly<Record<string, Float32Array>>, names: readonly string[], rate: number): void {
    this.#sampleRate = rate;
    for (const name of names) {
      const column = channels[name];
      if (column === undefined) continue;
      let target = this.#columns.get(name);
      if (target === undefined) {
        target = [];
        this.#columns.set(name, target);
      }
      for (const value of column) target.push(value);
      if (target.length > this.#capacity) target.splice(0, target.length - this.#capacity);
    }
  }

  get(name: string): Float32Array | null {
    const column = this.#columns.get(name);
    return column === undefined || column.length === 0 ? null : Float32Array.from(column);
  }

  /** Every channel as one record, for the fusion and mBLL routines. */
  snapshot(): Record<string, Float32Array> {
    const out: Record<string, Float32Array> = {};
    for (const [name, values] of this.#columns) out[name] = Float32Array.from(values);
    return out;
  }

  get length(): number {
    let shortest = Number.POSITIVE_INFINITY;
    for (const column of this.#columns.values()) shortest = Math.min(shortest, column.length);
    return Number.isFinite(shortest) ? shortest : 0;
  }

  clear(): void {
    this.#columns.clear();
    this.#sampleRate = 0;
  }
}

/** Colour per band, drawn from the page's own tokens. */
export type { Dominant } from "@musejs/signal";

export const BAND_TOKEN: Readonly<Record<EegBand, string>> = {
  delta: "--band-delta",
  theta: "--band-theta",
  alpha: "--band-alpha",
  beta: "--band-beta",
  gamma: "--band-gamma",
};

export const BAND_GLYPH: Readonly<Record<EegBand, string>> = {
  delta: "δ",
  theta: "θ",
  alpha: "α",
  beta: "β",
  gamma: "γ",
};

/** Root-mean-square, used to judge whether the head is still. */
export function rms(values: Float32Array): number {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const value of values) sum += value * value;
  return Math.sqrt(sum / values.length);
}
